/**
 * Gemeinsame Kleinteile der Kapitelseite „Webinare & Produkte". Seit Stufe 0 des P1/P2-Umbaus (Plan
 * 2026-10-23-p1-p2-kpi-review-umbau) liegen die sektionsweiten Teile in `components/auswertungen/kennzahlTeile.tsx` —
 * diese Datei re-exportiert sie, damit bestehende Importe (P1, P2, P3) unverändert gelten. Darunter nur P1-eigene
 * Formate und Etiketten (P1-V3 § 2.0).
 */
import { FC, ReactNode, useEffect, useState } from 'react';
import { Box, Tooltip } from '@mui/material';
import { TERMS } from '../../../helpers/kpiDefinitions';
import type { ShowUpZeile, ZuordnungStand } from '../../../helpers/auswertungenData';
import { VergleichWert } from '../../../components/auswertungen/kennzahlTeile';
import type { Abstand } from './befunde';

export {
  AbweichungMarke,
  AnfosZelle,
  balkenHoehe,
  datum,
  Hinweis,
  int,
  Laden,
  ProduktZelle,
  Quote,
  saeulenBreite,
  useLaden,
  vorlaeufigSatz,
} from '../../../components/auswertungen/kennzahlTeile';

const WOCHENTAG = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

/** `2026-10-04 18:00:00` → „So 04.10.26" (Wochentag aus dem Kalenderdatum, ohne Zeitzonen-Umrechnung). */
export function tagMitWochentag(v: string | null | undefined): string {
  if (!v) return '–';
  const [j, m, t] = v.slice(0, 10).split('-').map(Number);
  const w = new Date(Date.UTC(j, m - 1, t)).getUTCDay();
  return `${WOCHENTAG[w]} ${String(t).padStart(2, '0')}.${String(m).padStart(2, '0')}.${String(j).slice(2)}`;
}

/** `2026-11-01` → „01.11." */
export const tagMonat = (v: string | null | undefined): string => (v ? `${v.slice(8, 10)}.${v.slice(5, 7)}.` : '–');

/** Kleines Etikett am Namen mit Erklärung (P1-V3 Regel 6: „Zahlen noch offen bis TT.MM.", „frei ab …", „geplant …"). */
export const NamensEtikett: FC<{ children: ReactNode; tip?: string }> = ({ children, tip }) => {
  const inhalt = (
    <Box
      component="span"
      // Darf umbrechen: „Zahlen noch offen bis 01.11." / „kein Webinar mehr, Nachfolger TSP" waren bei 267 px die breiteste
      // Stelle der Namensspalte (Browser-Abnahme 25.10.).
      sx={{ ml: 0.75, fontSize: 11, fontWeight: 400, color: 'text.secondary', whiteSpace: 'normal', cursor: tip ? 'help' : undefined }}
    >
      {children}
    </Box>
  );
  return tip ? (
    <Tooltip title={tip} arrow placement="top">
      {inhalt}
    </Tooltip>
  ) : (
    inhalt
  );
};

/**
 * Anzahl Anfos mit „davon N vorläufig" darunter — wie `AnfosZelle` (kennzahlTeile), aber die Unterzeile darf umbrechen
 * (rechtsbündige Zellen sind `nowrap`; die geteilte Zelle hielt die Produkte-Tabelle bei 267 px 97 px breit).
 */
export const AnfosMitVorlaeufig: FC<{ n: number; vorlaeufig?: number }> = ({ n, vorlaeufig = 0 }) => (
  <Box sx={{ lineHeight: 1.25 }}>
    {n.toLocaleString('de-DE')}
    {vorlaeufig > 0 && <Unter>davon {vorlaeufig.toLocaleString('de-DE')} vorläufig</Unter>}
  </Box>
);

/** Noch nicht zugeordnete Bestellungen eines Termins (Persona-Check 26.10.2026, B14/B52); 0 ohne Angabe. */
export const offenBeiTermin = (z: ZuordnungStand | undefined, webinarId: string): number => z?.je_termin?.[webinarId] ?? 0;

/** Erklärsatz zum Hinweis „Zuordnung läuft nach". `lauf` ist Berliner Zeit `YYYY-MM-DD HH:mm:ss`. */
export function zuordnungSatz(n: number, lauf: string | null | undefined): string {
  const wann = lauf ? `${tagMonat(lauf)} ${lauf.slice(11, 16)} Uhr` : 'unbekannt';
  const eins = n === 1;
  return `${n.toLocaleString('de-DE')} ${eins ? 'Bestellung' : 'Bestellungen'} mit Webinar-Kennung ${eins ? 'ist' : 'sind'} nach dem letzten Lauf der Webinar-Zuordnung (${wann}) eingegangen und ${eins ? 'zählt' : 'zählen'} noch bei keinem Termin. Die Zuordnung läuft stündlich zur vollen Stunde; danach erscheinen die Anfos und ihr Wert hier (die Seite rechnet höchstens 15 Minuten nach).`;
}

/** Hinweis am Termin, solange PL-X-Bestellungen nach dem letzten Zuordnungslauf eingegangen sind (eigene Zeile). */
export const ZuordnungLaeuft: FC<{ n: number; lauf?: string | null }> = ({ n, lauf }) =>
  n > 0 ? (
    <Tooltip title={zuordnungSatz(n, lauf)} arrow placement="top">
      <Box component="span" sx={{ display: 'block', fontSize: 11, lineHeight: 1.25, color: 'warning.dark', whiteSpace: 'normal', cursor: 'help' }}>
        Zuordnung läuft nach: {n.toLocaleString('de-DE')} {n === 1 ? 'Bestellung' : 'Bestellungen'} noch ohne Termin
      </Box>
    </Tooltip>
  ) : null;

/** „Zahlen noch offen bis 01.11." mit Erklärung (Regel 17). */
export const ZahlenOffen: FC<{ bis: string | null | undefined }> = ({ bis }) =>
  bis ? <NamensEtikett tip={TERMS.zahlenOffen.tip}>{`${TERMS.zahlenOffen.short} ${tagMonat(bis)}`}</NamensEtikett> : null;

/** Marke „geschätzt" an Zeilen mit Anfos auf Angeboten über dem m5-Preisdeckel (Till 23.10.). */
export const GeschaetztMarke: FC<{ anfos?: number }> = ({ anfos }) =>
  anfos && anfos > 0 ? (
    <NamensEtikett tip={`${TERMS.wertGeschaetzt.tip} Betrifft ${anfos} ${anfos === 1 ? 'Anfo' : 'Anfos'} dieser Zeile.`}>
      {TERMS.wertGeschaetzt.short}
    </NamensEtikett>
  ) : null;

/** Unterzeile in einer Zelle (Regel 1: höchstens zwei Zeilen; die Unterzeile gehört zum Wert). */
// `whiteSpace: normal`: rechtsbündige Zellen setzen `nowrap` (SortableTable) — ohne das lief jede Unterzeile über die
// Kartenbreite hinaus (Browser-Abnahme 25.10.: 390 px mobil, Muster P2 `NaechsteRechnungenKarte`).
export const Unter: FC<{ children: ReactNode }> = ({ children }) => (
  <Box component="span" sx={{ display: 'block', fontSize: 11, color: 'text.secondary', lineHeight: 1.25, whiteSpace: 'normal' }}>
    {children}
  </Box>
);

/**
 * Gründe, warum ein Termin keinen Show-up zeigt — EINE Stelle für Kapitelseite und Detailseite (die Entscheidung selbst
 * trifft das Backend in `showUpZeile`, Codex-Befund 25.10.).
 */
export const SHOWUP_GRUND: Record<NonNullable<ShowUpZeile['grund']>, string> = {
  messluecke: 'Messlücke Teilnahme: die Teilnahme-Erfassung des früheren Werkzeugs lief bei diesem Termin nicht vollständig; der Show-up wäre ein Messfehler.',
  tracking_warnung: 'Tracking-Warnung: für diesen Termin kamen kaum oder zu viele Teilnahme-Signale; der Show-up ist nicht belastbar.',
  migriert: 'Anmeldungen übernommen: die Anmeldungen dieses Termins wurden aus einer anderen Quelle übernommen, der Show-up ist nicht vergleichbar.',
};
export const KURZ_GRUND: Record<NonNullable<ShowUpZeile['grund']>, string> = {
  messluecke: 'Messlücke Teilnahme',
  tracking_warnung: 'Tracking-Warnung',
  migriert: 'Anmeldungen übernommen',
};

/** „—" mit Grund im Tooltip (Regel 7). */
export const Strich: FC<{ grund: string }> = ({ grund }) => (
  <Tooltip title={grund} arrow placement="top">
    <Box component="span" sx={{ cursor: 'help' }}>
      —
    </Box>
  </Tooltip>
);

/** Kopfzeile eines zugeklappten Bereichs (E6-A): Pfeil, Titel, Zähl-Chips, Satz zum Handlungsbedarf. */
export const KlappKopf: FC<{ offen: boolean; onToggle: () => void; titel: string; satz: ReactNode; tip?: string; zuTun: boolean }> = ({
  offen,
  onToggle,
  titel,
  satz,
  tip,
  zuTun,
}) => (
  <Box
    component="button"
    type="button"
    aria-expanded={offen}
    onClick={onToggle}
    title={tip}
    sx={{
      display: 'flex',
      alignItems: 'baseline',
      flexWrap: 'wrap',
      gap: 1,
      width: '100%',
      textAlign: 'left',
      border: 1,
      borderColor: 'divider',
      borderRadius: '8px',
      px: 1.5,
      py: 1,
      background: 'none',
      font: 'inherit',
      color: 'inherit',
      cursor: 'pointer',
    }}
  >
    <Box component="span" sx={{ fontWeight: 600 }}>
      {offen ? '▾' : '▸'} {titel}
    </Box>
    <Box component="span" sx={{ fontSize: 13, color: zuTun ? 'text.primary' : 'text.secondary' }}>
      {satz}
    </Box>
  </Box>
);

// ─── gegen typisch (Persona-Umbau B1/B9, 28.10.2026) ─────────────────────────────────────────

/** Pfeilfarben wie `AbweichungMarke` (kennzahlTeile) — vizStyle hat keine Gut/Schlecht-Farben. */
const PFEIL_FARBE = { up: '#2e7d32', down: '#c62828' } as const;

/**
 * Zeile unter einer Kachel: „▲ +30 % · Vergleich typisch 5,7 T€". `offen`: Zahlen des Termins wachsen noch — der Pfeil
 * steht dann grau (Anfos und Wert eines jungen Termins liegen meist noch unter typisch). `neutral`: Pfeil ohne Gut/Schlecht-
 * Farbe (z. B. Anteil „nie gezahlt", wo „höher" schlechter ist). `wort`: Vergleichswort statt „typisch" (z. B. „Schnitt").
 */
export const GegenTypisch: FC<{ abstand: Abstand | null; typischText: string; offen?: boolean; neutral?: boolean; wort?: string; tip?: string }> = ({
  abstand,
  typischText,
  offen = false,
  neutral = false,
  wort = 'typisch',
  tip,
}) => (
  <Box component="span" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 0.75, rowGap: 0.25, mt: 0.5 }}>
    {abstand && (
      <Box
        component="span"
        title={offen ? TERMS.zahlenOffen.tip : undefined}
        aria-label={abstand.richtung === 'up' ? `über ${wort}` : abstand.richtung === 'down' ? `unter ${wort}` : `etwa ${wort}`}
        sx={{ whiteSpace: 'nowrap', fontWeight: 600, color: offen || neutral || abstand.richtung === 'gleich' ? 'text.secondary' : PFEIL_FARBE[abstand.richtung] }}
      >
        {abstand.richtung === 'up' ? '▲ ' : abstand.richtung === 'down' ? '▼ ' : '≈ '}
        {abstand.text}
      </Box>
    )}
    <VergleichWert tip={tip ?? `${TERMS.gegenTypisch.tip} ${TERMS.typischerTermin.tip}`}>
      {wort} {typischText}
    </VergleichWert>
  </Box>
);

// ─── Workshop P (29.10.2026): Umschalter, die neu laden ──────────────────────────────────────

/**
 * Lädt bei jedem neuen Wert des Umschalters neu (E1 Marken, E3 Zeitraum). Beim Wechsel wird die alte Antwort SOFORT
 * verworfen — sonst stünden die Zahlen der alten Wahl unter dem neuen Titel, bis die Antwort kommt (Muster
 * `kanaele/shared.useLadenFenster`). Abbruch-Flag gegen späte Antworten.
 */
export function useLadenMit<T, P>(fetcher: (p: P) => Promise<T>, param: P): { data: T | null; fehler: string | null } {
  const [data, setData] = useState<T | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    setData(null);
    setFehler(null);
    fetcher(param)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e: Error) => {
        if (!cancelled) setFehler(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [fetcher, param]);
  return { data, fehler };
}
